import {
  applyChange,
  clearSyncedTables,
  type SqlExecutor,
  type SyncBootstrapSnapshot,
  type SyncDelta,
  syncedTableNames,
} from '@couplespace/sync';

import {
  clearSearchIndex,
  ensureFtsTable,
  rebuildAllSearchIndex,
  reindexBlockSearchCascade,
} from './search-index';
import type { LocalDatabase } from './types';

export const SHARED_MIGRATION = `
CREATE TABLE IF NOT EXISTS members (
  id TEXT PRIMARY KEY NOT NULL,
  display_name TEXT NOT NULL,
  avatar_url TEXT,
  telegram_user_id TEXT,
  role TEXT DEFAULT 'member' NOT NULL,
  created_at INTEGER NOT NULL,
  updated_at INTEGER NOT NULL,
  vector_clock TEXT NOT NULL,
  sync_version INTEGER DEFAULT 0 NOT NULL
);
CREATE UNIQUE INDEX IF NOT EXISTS members_telegram_user_id_unique ON members (telegram_user_id);
CREATE TABLE IF NOT EXISTS blocks (
  id TEXT PRIMARY KEY NOT NULL,
  parent_id TEXT,
  position REAL DEFAULT 0 NOT NULL,
  type TEXT NOT NULL,
  title TEXT,
  content TEXT DEFAULT '{}' NOT NULL,
  icon TEXT,
  created_at INTEGER NOT NULL,
  updated_at INTEGER NOT NULL,
  deleted_at INTEGER,
  created_by TEXT,
  updated_by TEXT,
  actor_kind TEXT DEFAULT 'human' NOT NULL,
  vector_clock TEXT NOT NULL,
  sync_version INTEGER DEFAULT 0 NOT NULL,
  FOREIGN KEY (parent_id) REFERENCES blocks(id) ON DELETE SET NULL,
  FOREIGN KEY (created_by) REFERENCES members(id),
  FOREIGN KEY (updated_by) REFERENCES members(id)
);
CREATE TABLE IF NOT EXISTS block_properties (
  id TEXT PRIMARY KEY NOT NULL,
  block_id TEXT NOT NULL,
  property_id TEXT NOT NULL,
  value TEXT NOT NULL,
  created_at INTEGER NOT NULL,
  updated_at INTEGER NOT NULL,
  updated_by TEXT,
  actor_kind TEXT DEFAULT 'human' NOT NULL,
  vector_clock TEXT NOT NULL,
  sync_version INTEGER DEFAULT 0 NOT NULL,
  FOREIGN KEY (block_id) REFERENCES blocks(id) ON DELETE CASCADE,
  FOREIGN KEY (updated_by) REFERENCES members(id)
);
CREATE UNIQUE INDEX IF NOT EXISTS block_properties_block_property_unique ON block_properties (block_id, property_id);
CREATE TABLE IF NOT EXISTS databases (
  id TEXT PRIMARY KEY NOT NULL,
  block_id TEXT NOT NULL,
  created_at INTEGER NOT NULL,
  updated_at INTEGER NOT NULL,
  vector_clock TEXT NOT NULL,
  sync_version INTEGER DEFAULT 0 NOT NULL,
  FOREIGN KEY (block_id) REFERENCES blocks(id) ON DELETE CASCADE
);
CREATE UNIQUE INDEX IF NOT EXISTS databases_block_id_unique ON databases (block_id);
CREATE TABLE IF NOT EXISTS database_schemas (
  id TEXT PRIMARY KEY NOT NULL,
  database_id TEXT NOT NULL,
  columns TEXT NOT NULL,
  schema_version INTEGER DEFAULT 1 NOT NULL,
  created_at INTEGER NOT NULL,
  updated_at INTEGER NOT NULL,
  updated_by TEXT,
  actor_kind TEXT DEFAULT 'human' NOT NULL,
  vector_clock TEXT NOT NULL,
  sync_version INTEGER DEFAULT 0 NOT NULL,
  FOREIGN KEY (database_id) REFERENCES databases(id) ON DELETE CASCADE,
  FOREIGN KEY (updated_by) REFERENCES members(id)
);
CREATE TABLE IF NOT EXISTS relations (
  id TEXT PRIMARY KEY NOT NULL,
  from_block_id TEXT NOT NULL,
  to_block_id TEXT NOT NULL,
  kind TEXT NOT NULL,
  created_at INTEGER NOT NULL,
  vector_clock TEXT NOT NULL,
  sync_version INTEGER DEFAULT 0 NOT NULL,
  FOREIGN KEY (from_block_id) REFERENCES blocks(id) ON DELETE CASCADE,
  FOREIGN KEY (to_block_id) REFERENCES blocks(id) ON DELETE CASCADE
);
CREATE UNIQUE INDEX IF NOT EXISTS relations_from_to_kind_unique ON relations (from_block_id, to_block_id, kind);
CREATE TABLE IF NOT EXISTS block_acl (
  id TEXT PRIMARY KEY NOT NULL,
  block_id TEXT NOT NULL,
  member_id TEXT NOT NULL,
  can_read INTEGER DEFAULT 0 NOT NULL,
  can_write INTEGER DEFAULT 0 NOT NULL,
  created_at INTEGER NOT NULL,
  updated_at INTEGER NOT NULL,
  vector_clock TEXT NOT NULL,
  sync_version INTEGER DEFAULT 0 NOT NULL,
  FOREIGN KEY (block_id) REFERENCES blocks(id) ON DELETE CASCADE,
  FOREIGN KEY (member_id) REFERENCES members(id) ON DELETE CASCADE
);
CREATE UNIQUE INDEX IF NOT EXISTS block_acl_block_member_unique ON block_acl (block_id, member_id);
CREATE TABLE IF NOT EXISTS file_objects (
  id TEXT PRIMARY KEY NOT NULL,
  block_id TEXT,
  object_key TEXT NOT NULL,
  filename TEXT NOT NULL,
  mime_type TEXT NOT NULL,
  size_bytes INTEGER NOT NULL,
  created_at INTEGER NOT NULL,
  created_by TEXT,
  vector_clock TEXT NOT NULL,
  sync_version INTEGER DEFAULT 0 NOT NULL,
  FOREIGN KEY (block_id) REFERENCES blocks(id) ON DELETE SET NULL,
  FOREIGN KEY (created_by) REFERENCES members(id)
);
CREATE TABLE IF NOT EXISTS sync_meta (
  key TEXT PRIMARY KEY NOT NULL,
  value TEXT NOT NULL
);
CREATE TABLE IF NOT EXISTS sync_outbox (
  id TEXT PRIMARY KEY NOT NULL,
  table_name TEXT NOT NULL,
  operation TEXT NOT NULL,
  row_json TEXT NOT NULL,
  base_sync_version INTEGER,
  created_at INTEGER NOT NULL
);
CREATE TABLE IF NOT EXISTS sync_quarantine (
  table_name TEXT NOT NULL,
  row_id TEXT NOT NULL,
  seq INTEGER NOT NULL,
  operation TEXT NOT NULL,
  delta_json TEXT NOT NULL,
  error TEXT,
  attempts INTEGER DEFAULT 1 NOT NULL,
  first_seen INTEGER NOT NULL,
  last_attempt INTEGER NOT NULL,
  PRIMARY KEY (table_name, row_id)
);
CREATE INDEX IF NOT EXISTS blocks_nav_tree
ON blocks (parent_id, position, id, title, icon, created_by, type)
WHERE deleted_at IS NULL AND type IN ('page', 'database');
`;

export function createExecutor(database: LocalDatabase): SqlExecutor {
  return {
    async run(sql: string, params: unknown[] = []) {
      await database.runAsync(sql, ...(params as (string | number | null)[]));
    },
  };
}

export async function runMigrations(database: LocalDatabase): Promise<void> {
  const statements = SHARED_MIGRATION.split(';')
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && !s.startsWith('--'));
  for (const statement of statements) {
    await database.execAsync(`${statement};`);
  }

  const ftsReady = await ensureFtsTable(database);

  const [row] = await database.getAllAsync<{ value: string }>(
    `SELECT value FROM sync_meta WHERE key = 'schema_version'`,
  );
  if (!row) {
    await database.runAsync(`INSERT INTO sync_meta (key, value) VALUES ('schema_version', '1')`);
  }

  const version = Number(row?.value ?? '1');
  if (version < 2 && ftsReady) {
    await rebuildAllSearchIndex(database);
    await database.runAsync(
      `INSERT OR REPLACE INTO sync_meta (key, value) VALUES ('schema_version', '2')`,
    );
    await database.runAsync(
      `INSERT OR REPLACE INTO sync_meta (key, value) VALUES ('search_index_built', '1')`,
    );
  } else if (version < 2) {
    await database.runAsync(
      `INSERT OR REPLACE INTO sync_meta (key, value) VALUES ('schema_version', '2')`,
    );
  }
}

export async function getMeta(database: LocalDatabase, key: string): Promise<string | null> {
  const row = await database.getFirstAsync<{ value: string }>(
    `SELECT value FROM sync_meta WHERE key = ?`,
    key,
  );
  return row?.value ?? null;
}

export async function setMeta(database: LocalDatabase, key: string, value: string): Promise<void> {
  await database.runAsync(
    `INSERT OR REPLACE INTO sync_meta (key, value) VALUES (?, ?)`,
    key,
    value,
  );
}

export async function getLastSeq(database: LocalDatabase): Promise<number> {
  const value = await getMeta(database, 'last_seq');
  return value ? Number(value) : 0;
}

export async function setLastSeq(database: LocalDatabase, seq: number): Promise<void> {
  await setMeta(database, 'last_seq', String(seq));
}

export async function applyBootstrap(
  database: LocalDatabase,
  snapshot: SyncBootstrapSnapshot,
): Promise<void> {
  const exec = createExecutor(database);
  await clearSyncedTables(exec);
  await clearSearchIndex(database);
  for (const table of syncedTableNames) {
    const rows = snapshot.tables[table] ?? [];
    for (const payload of rows) {
      await applyChange(exec, {
        seq: snapshot.seq,
        table,
        rowId: String(payload.id),
        operation: 'insert',
        changedBy: '',
        changedAt: new Date().toISOString(),
        payload,
      });
    }
  }
  await rebuildAllSearchIndex(database);
  await database.runAsync(
    `INSERT OR REPLACE INTO sync_meta (key, value) VALUES ('search_index_built', '1')`,
  );
  await setLastSeq(database, snapshot.seq);
}

async function reindexBlocks(database: LocalDatabase, blockIds: Set<string>): Promise<void> {
  for (const blockId of blockIds) {
    try {
      await reindexBlockSearchCascade(database, blockId);
    } catch (err) {
      console.error('[CS-SYNC] failed to reindex block', blockId, err);
    }
  }
}

/**
 * Re-apply anything sitting in quarantine. A delta may have failed only because
 * a row it depended on had not arrived yet; once later batches fill that in, the
 * retry succeeds and the entry is removed. Entries that keep failing stay
 * quarantined (and counted) rather than being lost.
 */
export async function drainQuarantine(database: LocalDatabase): Promise<void> {
  const { listQuarantine, clearQuarantine, quarantineDelta } = await import('./quarantine.js');
  const entries = await listQuarantine(database);
  if (entries.length === 0) {
    return;
  }
  const exec = createExecutor(database);
  const blockIds = new Set<string>();
  for (const entry of entries) {
    try {
      await applyChange(exec, entry.delta);
      await clearQuarantine(database, entry.tableName, entry.rowId);
      if (entry.delta.table === 'blocks') {
        blockIds.add(entry.rowId);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      await quarantineDelta(database, entry.delta, message);
    }
  }
  await reindexBlocks(database, blockIds);
}

export async function applyDeltas(database: LocalDatabase, deltas: SyncDelta[]): Promise<number> {
  if (deltas.length === 0) {
    return await getLastSeq(database);
  }
  const { clearQuarantine, quarantineDelta, quarantinedKeySet } = await import('./quarantine.js');
  const exec = createExecutor(database);
  const blockIds = new Set<string>();
  // Rows currently in quarantine — so a newer, good delta for the same row
  // supersedes (and clears) an earlier failure instead of leaving stale data.
  const quarantined = await quarantinedKeySet(database);

  // Apply each delta independently so one malformed row can't abort the whole
  // batch (and, because the cursor below is then never advanced, wedge sync in
  // an endless re-fetch loop). Failures are quarantined — kept for retry and
  // surfaced to the user — rather than dropped, which used to silently diverge
  // this client from the server.
  for (const delta of deltas) {
    try {
      await applyChange(exec, delta);
      if (delta.table === 'blocks') {
        blockIds.add(delta.rowId);
      }
      if (quarantined.has(`${delta.table}\u0000${delta.rowId}`)) {
        await clearQuarantine(database, delta.table, delta.rowId);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error('[CS-SYNC] failed to apply delta', delta.table, delta.rowId, delta.seq, err);
      await quarantineDelta(database, delta, message);
    }
  }
  await reindexBlocks(database, blockIds);
  const maxSeq = Math.max(...deltas.map((d) => d.seq));
  await setLastSeq(database, maxSeq);
  // A row that failed earlier may now apply thanks to rows in this batch.
  await drainQuarantine(database);
  return maxSeq;
}

export type { SyncDelta, SyncBootstrapSnapshot };
